import { Gem, ShieldCheck, PackageCheck, Scissors, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal } from "@/components/landing/Reveal";
import { scrollToId, PHONE, PHONE_TEL } from "@/lib/site";

const POINTS = [
  { icon: Gem, title: "Hand-inspected cleaning", text: "Every gown is examined bead-by-bead and cleaned with gentle, fabric-safe processes." },
  { icon: PackageCheck, title: "Preservation boxing", text: "Acid-free tissue and a museum-style keepsake box protect your gown for decades." },
  { icon: ShieldCheck, title: "Stain & hem specialists", text: "Grass, champagne, and dance-floor hems — treated before they set for good." },
  { icon: Scissors, title: "Alterations & fittings", text: "Pre-wedding tailoring and post-wedding resizing by our in-house seamstresses." },
];

export const WeddingDress = () => (
  <section id="wedding-dress" data-testid="wedding-section" className="bg-white py-24 lg:py-32">
    <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2">
      <Reveal className="order-2 lg:order-1">
        <div className="overflow-hidden rounded-[2rem] border border-slate-200 shadow-2xl shadow-brand-blue/10">
          <img
            data-testid="wedding-dress-image"
            src="/images/wedding_gown.png"
            onError={(e) => (e.currentTarget.style.display = "none")}
            alt="Elegant wedding gown professionally cleaned and preserved"
            className="aspect-[4/5] w-full object-cover transition-transform duration-700 hover:scale-105"
          />
        </div>
      </Reveal>

      <Reveal delay={0.1} className="order-1 lg:order-2">
        <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-blue sm:text-sm">Wedding dresses</p>
        <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-brand-navy sm:text-3xl lg:text-4xl">
          Your gown carried the day. <span className="text-outline-navy block font-extrabold">We&apos;ll carry it forever.</span>
        </h2>
        <p className="mt-5 max-w-lg text-base leading-relaxed text-slate-600">
          From pre-ceremony pressing to lifetime preservation, Oxford Cleaners treats every gown like
          it&apos;s our own family&apos;s — because it is. Free pickup &amp; delivery included, and every
          preservation starts with a personal consultation.
        </p>

        <div className="mt-8 grid gap-5 sm:grid-cols-2">
          {POINTS.map((p) => (
            <div key={p.title} className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
              <p.icon className="h-6 w-6 text-fall-orange" />
              <h3 className="mt-3 font-display text-base font-bold text-brand-navy">{p.title}</h3>
              <p className="mt-1 text-sm leading-relaxed text-slate-600">{p.text}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <button
            data-testid="wedding-book-button"
            onClick={() => scrollToId("book")}
            className="rounded-full bg-brand-blue px-7 py-3.5 font-display text-sm font-extrabold uppercase tracking-widest text-white shadow-lg shadow-brand-blue/30 transition-transform hover:scale-105"
          >
            Book gown pickup
          </button>
          <a
            data-testid="wedding-call-link"
            href={PHONE_TEL}
            className="rounded-full border-2 border-brand-navy px-7 py-3 text-sm font-bold text-brand-navy transition-colors hover:bg-brand-navy hover:text-white"
          >
            Call {PHONE} for a quote
          </a>
        </div>
        <p className="mt-4">
          <Link
            data-testid="wedding-page-link"
            to="/wedding-dress-cleaning-preservation"
            className="text-sm font-bold text-brand-blue underline decoration-fall-amber decoration-2 underline-offset-4 transition-colors hover:text-brand-blue-hover"
          >
            Explore gown cleaning, preservation &amp; pricing FAQs <ArrowRight className="inline h-4 w-4" />
          </Link>
        </p>
      </Reveal>
    </div>
  </section>
);
